import type { ChecklistItem, Recurrence, Task } from '@nexo/types';
import { IconButton, Progress, Select, cn } from '@nexo/ui';
import { ListChecks, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { recurrenceText } from './taskUtils';
import { useUpdateTask } from './useTaskMutations';

const newId = () => Math.random().toString(36).slice(2, 10);

/** Checklist of a task: tick, add, rename and remove items (saved immediately). */
export function TaskChecklist({ task }: { task: Task }) {
  const { t } = useTranslation();
  const update = useUpdateTask();
  const [draft, setDraft] = useState('');
  const items = task.checklist;
  const done = items.filter((i) => i.done).length;
  const save = (checklist: ChecklistItem[]) => update.mutate({ id: task.id, input: { checklist } });

  const add = () => {
    const text = draft.trim();
    if (!text) return;
    save([...items, { id: newId(), text, done: false }]);
    setDraft('');
  };

  return (
    <section className="mt-6">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <ListChecks className="size-4 text-muted" /> {t('Checklist')}
        {items.length > 0 && (
          <span className="text-xs font-normal text-muted tabular-nums">
            {done}/{items.length}
          </span>
        )}
      </h3>
      {items.length > 0 && (
        <Progress
          value={done}
          max={items.length}
          label={t('Progreso de la checklist')}
          className="mb-2"
        />
      )}
      <ul className="flex flex-col">
        {items.map((item) => (
          <li
            key={item.id}
            className="group flex items-center gap-2.5 rounded-lg px-1 py-1 hover:bg-surface-2"
          >
            <input
              type="checkbox"
              checked={item.done}
              aria-label={item.text}
              onChange={() =>
                save(items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)))
              }
              className="size-4 accent-[var(--color-accent)]"
            />
            <span className={cn('flex-1 text-sm', item.done && 'text-subtle line-through')}>
              {item.text}
            </span>
            <IconButton
              label={t('Quitar «{{text}}»', { text: item.text })}
              size="sm"
              className="invisible group-hover:visible"
              onClick={() => save(items.filter((i) => i.id !== item.id))}
            >
              <X />
            </IconButton>
          </li>
        ))}
      </ul>
      {items.length < 50 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
          className="mt-1 flex items-center gap-2 px-1"
        >
          <Plus className="size-4 text-subtle" />
          <input
            value={draft}
            maxLength={200}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('Añadir elemento…')}
            aria-label={t('Nuevo elemento de la checklist')}
            className="h-8 flex-1 bg-transparent text-sm placeholder:text-subtle focus:outline-none"
          />
        </form>
      )}
    </section>
  );
}

type Preset = 'none' | 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly';

const presets: Record<Exclude<Preset, 'none'>, (due: string) => Recurrence> = {
  daily: () => ({ frequency: 'daily', interval: 1, weekdays: [] }),
  weekdays: () => ({ frequency: 'weekly', interval: 1, weekdays: [1, 2, 3, 4, 5] }),
  weekly: (due) => ({
    frequency: 'weekly',
    interval: 1,
    weekdays: [new Date(`${due}T00:00:00Z`).getUTCDay()],
  }),
  biweekly: (due) => ({
    frequency: 'weekly',
    interval: 2,
    weekdays: [new Date(`${due}T00:00:00Z`).getUTCDay()],
  }),
  monthly: () => ({ frequency: 'monthly', interval: 1, weekdays: [] }),
};

/** Spanish keys: translate at render with `t()`. */
const presetLabels: Record<Preset, string> = {
  none: 'No se repite',
  daily: 'Cada día',
  weekdays: 'Días laborables (lun–vie)',
  weekly: 'Cada semana',
  biweekly: 'Cada 2 semanas',
  monthly: 'Cada mes',
};

function currentPreset(r: Recurrence | null): Preset | null {
  if (!r) return 'none';
  if (r.frequency === 'daily' && r.interval === 1) return 'daily';
  if (r.frequency === 'monthly' && r.interval === 1) return 'monthly';
  if (r.frequency === 'weekly' && r.weekdays.join() === '1,2,3,4,5') return 'weekdays';
  if (r.frequency === 'weekly' && r.weekdays.length <= 1)
    return r.interval === 2 ? 'biweekly' : r.interval === 1 ? 'weekly' : null;
  return null;
}

/** "Repetir" property: needs a due date (the next occurrence is computed from it). */
export function RecurrencePicker({ task }: { task: Task }) {
  const { t } = useTranslation();
  const update = useUpdateTask();
  const preset = currentPreset(task.recurrence);
  if (!task.dueDate) {
    return (
      <span className="inline-flex h-8 items-center px-2 text-subtle">
        {t('Pon una fecha límite')}
      </span>
    );
  }
  const due = task.dueDate;
  return (
    <Select
      aria-label={t('Repetir')}
      value={preset ?? 'custom'}
      onChange={(value) => {
        if (value === 'custom') return;
        const next = value as Preset;
        update.mutate({
          id: task.id,
          input: { recurrence: next === 'none' ? null : presets[next](due) },
        });
      }}
      variant="ghost"
      size="sm"
      options={[
        ...(preset === null && task.recurrence
          ? [{ value: 'custom', label: recurrenceText(task.recurrence) }]
          : []),
        ...(Object.keys(presetLabels) as Preset[]).map((key) => ({
          value: key,
          label: t(presetLabels[key]),
        })),
      ]}
    />
  );
}
